import React, { useRef, useEffect } from "react";
import cn from "classnames";
import SearchResultLoader from "../ui/loaders/search-result-loader";
import { useUI } from "@/contexts/ui.context";
import SearchBox from "./search-box";
import SearchProduct from "./search-product";
import { sanitize } from "@/framework/utils/sanitize";
import { useScrollBlock } from "@/utils/scroll-block";
import useOnClickOutside from "@/utils/use-click-outside";
import { products } from "@/data/products";
export default function Search() {
  const { displaySearch, closeSearch, showToast } = useUI();
  const searchContainerRef = useRef(null);
  const [searchText, setSearchText] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [fetching, setFetching] = React.useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    if (displaySearch) {
      blockScroll();
    } else {
      allowScroll();
    }
  }, [displaySearch, blockScroll, allowScroll]);

  // Auto-fill for demo
  useEffect(() => {
    if (displaySearch) {
      setSearchText("platter");
    }
  }, [displaySearch]);

  useOnClickOutside(searchContainerRef, () => closeSearch());

  // 🔍 Local search instead of GraphQL
  useEffect(() => {
    if (!searchText || searchText.length < 3) {
      setResults([]);
      return;
    }

    setFetching(true);

    // simulate async (like graphql) for loader effect
    const timeout = setTimeout(() => {
      const filtered = products.filter((p) =>
        p.attributes.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setResults(filtered.map((r) => sanitize(r)));
      setFetching(false);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchText]);

  function handleSearch(data) {
    setSearchText(data.search);
  }
  function handleAutoSearch(e) {
    e.preventDefault();
    setSearchText(e.currentTarget.value);
  }
  function clear() {
    setSearchText("");
    setResults([]);
  }

  return (
    <div
      className={cn(
        {
          "visible opacity-100": displaySearch,
          "opacity-0 hidden": !displaySearch,
        },
        "fixed bg-black/50 top-0 left-0 z-[99999] transition duration-300 ease-in-out px-4 w-full h-screen"
      )}
    >
      <div
        className="w-full md:w-[730px] lg:w-[930px] mx-auto"
        ref={searchContainerRef}
      >
        <div className="mt-3.5 lg:mt-4 w-full">
          <div className="flex flex-col mx-auto mb-1.5 w-full ">
            <SearchBox
              onSubmit={handleSearch}
              onChange={handleAutoSearch}
              name="search"
              value={searchText}
              onClear={clear}
              ref={(input) => input && input.focus()}
            />
          </div>
          {searchText && (
            <div className="h-[500px] overflow-y-scroll bg-white rounded-md">
              {fetching ? (
                <div className="p-5 border-b border-gray-300 border-opacity-30 last:border-b-0 flex flex-col gap-2">
                  {Array.from({ length: 20 }).map((_, idx) => (
                    <SearchResultLoader
                      key={idx}
                      uniqueKey={`top-search-${idx}`}
                    />
                  ))}
                </div>
              ) : results.length > 0 ? (
                results.map((item, index) => (
                  <div
                    className="p-5 border-b border-gray-150 relative last:border-b-0"
                    onClick={closeSearch}
                    key={"searchItem" + index}
                  >
                    <SearchProduct item={item} />
                  </div>
                ))
              ) : (
                <div className="p-5 text-gray-500">No products found</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
